import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { LoginOrGuestSkeleton } from '@ynap/login-and-registration-form'
import { default as LoginOrGuestForm } from '@ynap/login-and-registration-form/src/LoginOrGuestForm/LoginOrGuestForm.js'
import BEMHelper from '@ynap/bem-helper'
import track from 'react-tracking'
import { trackPageEvent, trackGuestCheckout, trackLoggedInUserCheckout, trackLoggedInFailed, trackOptIn } from './loginTrackEvents'
import { ScrollToTopOnMount } from '@ynap/scroll-to-top'
import { GuestIdentityReissuedError } from '@ynap/fetch'
import { raygun } from '@ynap/clientside-monitoring'
import { withOptimizely } from '@ynap/optimizely-utils'
import { getCookie } from '@ynap/redux-cookies-middleware/src/cookieApi'

import { getGenderCartContents } from './utils'
import { Notification } from '@ynap/notification'
import { initKlarnaExpressFlow } from '../../utils/klarnaUtils'
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay'

const bem = new BEMHelper('LoginStep')

@track({
    category: {
        primaryCategory: 'sign in',
        subCategory: 'checkout',
    },
})
class LoginStep extends Component {
    static propTypes = {
        userStatus: PropTypes.string,
        redirect: PropTypes.string,
        returnUrl: PropTypes.string,
        lowQualitySrc: PropTypes.string,
        highQualitySrc: PropTypes.string,
        fetchCart: PropTypes.func.isRequired,
        fastCheckout: PropTypes.func.isRequired,
        login: PropTypes.func.isRequired,
        createFastUser: PropTypes.func.isRequired,
        placeholder: PropTypes.func,
        history: PropTypes.shape({
            push: PropTypes.func.isRequired,
        }).isRequired,
        messages: PropTypes.shape({
            loginStep: PropTypes.shape({
                title: PropTypes.func.isRequired,
                subTitle: PropTypes.func.isRequired,
            }),
        }),
        links: PropTypes.shape({
            termsAndConditions: PropTypes.string.isRequired,
            privacyPolicy: PropTypes.string.isRequired,
        }).isRequired,
        deviceType: PropTypes.string,
    }

    constructor() {
        super()
        this.state = {
            maMarketingConsent: null,
            shouldLogin: undefined,
            callCheckout: true,
            isLoadingPopupVisible: false,
        }
        this.userLogin = this.userLogin.bind(this)
        this.userRegister = this.userRegister.bind(this)
        this.createFastUser = this.createFastUser.bind(this)
        this.handleToggleConsent = this.handleToggleConsent.bind(this)
    }

    componentDidMount() {
        const { userStatus, fetchCart } = this.props

        if (!userStatus) {
            return fetchCart()
        }
        this.redirectIfRegistered()
    }

    componentDidUpdate(prevProps) {
        const { userStatus } = this.props

        if (prevProps.userStatus !== userStatus && this.state.callCheckout) {
            this.redirectIfRegistered()
        }
    }

    componentWillUnmount() {
        const { setKlarnaExpress } = this.props
        setKlarnaExpress({ values: null })
    }

    redirectIfRegistered() {
        const { userStatus, fastCheckout, history, redirect, returnUrl, logonId, tracking, klarnaExpress } = this.props
        const shouldLogin = !((redirect && userStatus === 'R') || (userStatus === 'G' && logonId))
        this.setState({ shouldLogin })

        if (shouldLogin) {
            tracking.trackEvent(trackPageEvent(this.props))
        } else {
            return fastCheckout({ returnUrl })
                .then(() => klarnaExpress?.data && this.klarnaExpressCheckout())
                .catch((error) => {
                    // allow to continue with checkout if fast checkout fails
                    if (error?.message !== 'APPLY_FASTCHK_PAYMENT_FAILED') throw error
                })
                .then(
                    () => history.push(redirect),
                    (error) => {
                        this.setState({ shouldLogin: true })
                    }
                )
        }
    }

    @track(({ checkoutAnalytics }, _state, [ marketingConsentValue, isGuestCheckout ]) => {
        return trackOptIn(checkoutAnalytics, marketingConsentValue, isGuestCheckout)
    })
    trackOptIn(_maMarketingConsent, _isGuestCheckout) {}

    @track(({ checkoutAnalytics }, _state, _, [_resolved, rejected]) => {
        if (rejected) {
            return trackLoggedInFailed(checkoutAnalytics)
        }

        return trackLoggedInUserCheckout(checkoutAnalytics)
    })
    userLogin(values) {
        const { login, fastCheckout, returnUrl, redirect, history, optimizely, klarnaExpress } = this.props
        return login(values, undefined, undefined, false)
            .then(() => {
                this.setState({ callCheckout: false })
                optimizely?.track('user_login_account')
                return fastCheckout({ returnUrl })
            })
            .then(() => klarnaExpress?.data && this.klarnaExpressCheckout())
            .catch((error) => {
                this.setState({ callCheckout: true })
                // allow to continue with checkout if fast checkout fails
                if (error?.message !== 'APPLY_FASTCHK_PAYMENT_FAILED') throw error
            })
            .then(() => history.push(redirect))
    }

    @track(({ checkoutAnalytics }, state) => trackGuestCheckout(state.maMarketingConsent, checkoutAnalytics))
    createFastUser(values) {
        const { redirect, history, optimizely } = this.props
        optimizely?.track('user_login_guest')
        return this.performFastUser(values).then(() => history.push(redirect))
    }

    performFastUser(values, shouldRetry = true) {
        const { createFastUser, fastCheckout, returnUrl, klarnaExpress } = this.props
        return createFastUser(values, false)
            .then(() => {
                this.trackOptIn(values?.maMarketingConsent === 'true', true)
                return fastCheckout({ returnUrl })
            })
            .then(() => klarnaExpress?.data && this.klarnaExpressCheckout())
            .catch((error) => {
                if (error instanceof GuestIdentityReissuedError) {
                    raygun.helpers.agent('send', {
                        error,
                        tags: ['fastCheckout', 'partialAuthenticated', `${shouldRetry ? '' : 'fastCheckoutRetried'}`],
                    })
                    if (shouldRetry) {
                        return this.performFastUser(values, false)
                    }
                }
                throw error
            })
    }

    klarnaExpressCheckout() {
        const { setShippingAddress, setPaymentMethod, locale, history, klarnaExpress, setKlarnaExpress } = this.props

        return initKlarnaExpressFlow({
            setShippingAddress,
            setPaymentMethod,
            locale,
            history,
            data: klarnaExpress.data,
            setKlarnaExpress,
            setLoadingState: (isLoadingPopupVisible) => this.setState({ isLoadingPopupVisible }),
        })
    }

    handleToggleConsent(maMarketingConsent) {
        this.setState({ maMarketingConsent })
    }

    userRegister(values, form, redirect) {
        const { register, history, fastCheckout, returnUrl, klarnaExpress } = this.props
        return register(values, form, false)
            .then(() => {
                this.setState({ callCheckout: false })
                this.trackOptIn(values?.maMarketingConsent === 'true')
                return fastCheckout({ returnUrl })
            })
            .then(() => klarnaExpress?.data && this.klarnaExpressCheckout())
            .catch((error) => {
                this.setState({ callCheckout: true })
                // allow to continue with checkout if fast checkout fails
                if (error?.message !== 'APPLY_FASTCHK_PAYMENT_FAILED') throw error
            })
            .then(() => history.push(redirect))
    }

    render() {
        const {
            userStatus,
            messages,
            placeholder,
            initialValues,
            orderItem,
            checkoutAnalytics,
            klarnaExpress,
            locale,
            config,
            multipleLanguageCountries,
        } = this.props

        const { isLoadingPopupVisible } = this.state
        const klarnaExpressFlowActive = !!klarnaExpress?.data
        const shouldDisplayPlaceholder = (!userStatus || !this.state.shouldLogin) && placeholder

        const { gdprLikeCountries, ageConsentCountries, tcConsentCountries, flags } = config || {}

        const gdprConsent = !!gdprLikeCountries?.includes(locale?.country)
        const ageConsent = !!ageConsentCountries?.includes(locale?.country) && messages.registrationForm.ageConsent?.()
        const tcConsent = !!tcConsentCountries?.includes(locale?.country) && messages.registrationForm.tcConsent?.()
        const genderPreferenceCookie = getCookie('ton-shop') || 'womens'
        const defaultGenderValue = { womens: 'Female', mens: 'Male' }[genderPreferenceCookie] || 'Unspecified'
        const { enableMarketingOptInCountryList } = flags || {}
        const optInRolloutInCountry = enableMarketingOptInCountryList?.includes(locale?.country)
        const initialMarketingConsent = optInRolloutInCountry?.toString()

        return (
            <ScrollToTopOnMount>
                {klarnaExpressFlowActive && (shouldDisplayPlaceholder || isLoadingPopupVisible) && (
                    <LoadingOverlay messages={[messages.basket?.loadingOverlay?.firstMessage?.(), messages.basket?.loadingOverlay?.secondMessage?.()]} />
                )}
                {shouldDisplayPlaceholder ? (
                    placeholder(this.props)
                ) : (
                    <div className={bem('loginWrapper')}>
                        <h2 className={bem('loginTitle')}>{messages.loginStep.signInOrGuest()}</h2>
                        <LoginOrGuestForm
                            {...this.props}
                            isSignInOpen={true}
                            login={this.userLogin}
                            register={this.userRegister}
                            createFastUser={this.createFastUser}
                            mergeCart="false"
                            handleToggleConsent={this.handleToggleConsent}
                            gender={orderItem ? getGenderCartContents(orderItem) : undefined}
                            initialValues={{
                                ...initialValues,
                                formType: 'login',
                                maMarketingConsent: initialMarketingConsent,
                                gender: defaultGenderValue,
                                registration: {
                                    ...(flags?.enableGenderSelection && {
                                        gender: defaultGenderValue,
                                        maMarketingConsent: initialMarketingConsent,
                                    }),
                                },
                            }}
                            checkoutAnalytics={checkoutAnalytics}
                            guestFastTrackEmail={klarnaExpress?.data?.email}
                            config={config}
                            multipleLanguageCountries={multipleLanguageCountries}
                            gdprConsent={gdprConsent}
                            ageConsent={ageConsent}
                            tcConsent={tcConsent}
                        />
                        {klarnaExpressFlowActive && (
                            <Notification className={bem('fastTrackMessage')} type="success" message={messages.loginStep?.fastTrackMessage?.()} />
                        )}
                    </div>
                )}
            </ScrollToTopOnMount>
        )
    }
}

export const LoginStepSkeleton = () => (
    <div className={bem('loginWrapper')}>
        <LoginOrGuestSkeleton />
    </div>
)

export default withOptimizely(LoginStep)
